import axios from 'axios';

// Normalize the configured API base URL. A scheme-less value (e.g.
// "desicaptions-production.up.railway.app") makes axios treat baseURL as a
// path on the current origin, producing requests like
// "https://www.desicaptions.com/desicaptions-production.up.railway.app/api/..."
// (405). Force https when no scheme is present and strip any trailing slash.
const rawBaseURL = import.meta.env.VITE_API_URL || '';
const baseURL = rawBaseURL && !/^https?:\/\//i.test(rawBaseURL)
  ? `https://${rawBaseURL.replace(/\/+$/, '')}`
  : rawBaseURL.replace(/\/+$/, '');

// Create an axios instance
const api = axios.create({
  baseURL, // Empty means relative path (Vite proxy)
  timeout: 3600000, // 1 hour
  withCredentials: true, // Needed to send/receive HTTP-only cookies (refresh token)
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Tell AuthContext the session is dead (invalid access token AND failed
// refresh) so it clears React state and PrivateRoute redirects to /login.
// Removing auth_token from localStorage alone is not enough: the app keeps
// rendering with the stale in-memory user until a full reload otherwise.
// Guarded on a token having existed so failed LOGIN attempts (401 with no
// session) don't fire a misleading "session expired".
const notifySessionExpired = () => {
  try { window.dispatchEvent(new Event('auth:session-expired')); } catch { /* non-browser env */ }
};

// Queue to hold requests while refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// If a 401 is received, try to refresh the token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) return Promise.reject(error);

    // A rejected login is a credential error, not an expired access token.
    // Refreshing here would replace the useful login error with a session error.
    if (/^\/api\/auth\/(login|google|register|forgot-password|reset-password|verify-email|resend-verification)(?:[/?]|$)/.test(originalRequest.url)) {
      return Promise.reject(error);
    }

    // Avoid infinite loop if refresh itself fails
    if (originalRequest.url === '/api/auth/refresh') {
      if (localStorage.getItem('auth_token')) {
        localStorage.removeItem('auth_token');
        notifySessionExpired();
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Queued requests also get only one retry. Otherwise a second 401 from
      // any queued request can start another refresh cycle.
      originalRequest._retry = true;
      if (isRefreshing) {
        // If already refreshing, queue this request until refresh finishes
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      isRefreshing = true;

      try {
        const { data } = await api.post('/api/auth/refresh');
        const newToken = data.token;
        
        // Save new token
        localStorage.setItem('auth_token', newToken);

        // Update auth header for this request
        originalRequest.headers.Authorization = 'Bearer ' + newToken;
        
        processQueue(null, newToken);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (localStorage.getItem('auth_token')) {
          localStorage.removeItem('auth_token');
          notifySessionExpired();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
